import capitalize from "lodash-es/capitalize"
import eq from "lodash-es/eq"
import isNull from "lodash-es/isNull"

import type { IUserCoachModel } from "@/3514/types"
import { isUserCoach, isUserManager } from "@/3514/utils"
import type { TStoredSubUser } from "@/services"

import {
    COACH_WELCOME_EMAIL_URI_PARAMETER_NAME,
    COACH_WELCOME_EMAIL_URI_PARAMETER_VALUE,
    COACH_WELCOME_PRODUCT_TYPE_URI_PARAMETER_NAME,
    COACH_WELCOME_USER_ID_URI_PARAMETER_NAME
} from "../config"

import { ECoachUserListItemVariant, type TCoachUserListItemToRender, type TEmailBannerConfig } from "./types"

function mapSubUserListAndCurrentUserToCoachListToRender({
    subUserList,
    currentUser,
    targetUserId
}: {
    subUserList: TStoredSubUser[]
    currentUser: IUserCoachModel
    targetUserId: number | null
}): TCoachUserListItemToRender[] {
    const participantListToRender: TCoachUserListItemToRender[] =
        subUserList?.map(
            ({ ...p }: TStoredSubUser): TCoachUserListItemToRender => ({
                ...p,
                color: p.avatarColor,
                description: "Explore the participant experience.",
                variant: eq(p.id, targetUserId)
                    ? ECoachUserListItemVariant.Highlighted
                    : ECoachUserListItemVariant.Default
            })
        ) ?? []

    const currentUserToRender: TCoachUserListItemToRender = {
        role: capitalize(currentUser.role),
        photo: currentUser.photo,
        color: undefined,
        id: currentUser?.userId ?? currentUser?.id,
        firstName: currentUser.firstName,
        lastName: currentUser.lastName,
        jwtPair: currentUser.jwtPair,
        ...(isUserManager(currentUser) && {
            variant: isNull(targetUserId) ? ECoachUserListItemVariant.Highlighted : ECoachUserListItemVariant.Default,
            description: "This is your normal program manager account"
        }),
        ...(isUserCoach(currentUser) && {
            variant: isNull(targetUserId) ? ECoachUserListItemVariant.Highlighted : ECoachUserListItemVariant.Default,
            description: "This is your normal coach account"
        })
    }

    return participantListToRender.length ? [currentUserToRender, ...participantListToRender] : [currentUserToRender]
}

function getEmailBannerConfigFromUri(params: URLSearchParams): TEmailBannerConfig {
    return {
        isVisible: params.get(COACH_WELCOME_EMAIL_URI_PARAMETER_NAME) === COACH_WELCOME_EMAIL_URI_PARAMETER_VALUE,
        productType: params.get(COACH_WELCOME_PRODUCT_TYPE_URI_PARAMETER_NAME)
    }
}

function getUserIdFromUri(params: URLSearchParams): number | null {
    const param: string = params.get(COACH_WELCOME_USER_ID_URI_PARAMETER_NAME)

    return isNull(param) ? null : Number(param)
}

type TUtils = {
    mapSubUserListAndCurrentUserToCoachListToRender({
        subUserList,
        currentUser,
        targetUserId
    }: {
        subUserList: TStoredSubUser[]
        currentUser: IUserCoachModel
        targetUserId: number | null
    }): TCoachUserListItemToRender[]
    getEmailBannerConfigFromUri(params: URLSearchParams): TEmailBannerConfig
    getUserIdFromUri(params: URLSearchParams): number | null
}

const utils: TUtils = {
    mapSubUserListAndCurrentUserToCoachListToRender,
    getEmailBannerConfigFromUri,
    getUserIdFromUri
}

export { utils as coachWelcomePageUtils, type TUtils as TCoachWelcomePageUtils }
